import { useSearchParams } from "react-router";
import config from "../config";

const STORAGE_KEY = "wc_contest_id";
// Cold-start fallback only (no URL param AND nothing stored). Per-host via
// config: the team-test host overrides this to a Knockout contest so a fresh
// visit there can't default to Group picks; everywhere else it stays 44.
const DEFAULT_CONTEST_ID = config.defaultContestId || 44;

// Resolve the active contest id and make it STICK, so a contest can never
// silently flip to another (e.g. a Knockout contest suddenly showing Group
// picks because the id fell back to a World Cup contest).
//
// In-app navigation (tab switches, redirects) doesn't always carry ?contest_id,
// and the instant it's missing a naive read falls back to the default contest
// (44 = a World Cup contest). We persist the id and recover it when the URL
// lacks it.
//
// Persistence uses localStorage, NOT sessionStorage: on mobile (iOS Safari in
// particular) sessionStorage is easily lost between loads and when the tab is
// backgrounded — which is exactly when the contest was flipping. localStorage
// survives navigation, reload, backgrounding, and reopening the link, so the
// id holds for the whole testing session.
//
// Resolution order:
//   1. explicit ?contest_id in the URL  (a deliberate switch always wins)
//   2. the last id this browser locked onto (localStorage)
//   3. default (44)
//
// Reading an explicit URL id re-persists it, so opening a different contest's
// link switches and re-locks; a browser that has only ever opened one contest's
// link stays on that contest no matter how navigation drops the param.
export const useContestId = () => {
  const [searchParams] = useSearchParams();
  const raw = searchParams.get("contest_id");
  const fromUrl = raw != null ? parseInt(raw, 10) : NaN;

  if (Number.isFinite(fromUrl) && fromUrl > 0) {
    try {
      localStorage.setItem(STORAGE_KEY, String(fromUrl));
    } catch (e) {
      /* localStorage unavailable (private mode, etc.) — ignore */
    }
    return fromUrl;
  }

  try {
    const stored = parseInt(localStorage.getItem(STORAGE_KEY), 10);
    if (Number.isFinite(stored) && stored > 0) return stored;
  } catch (e) {
    /* ignore */
  }

  return DEFAULT_CONTEST_ID;
};

export default useContestId;
